import { useMutation } from '@tanstack/react-query';
import { useFetchAccount } from './get-account';
import { Leverage, useAccountStore } from '@/store/use-account-store';
import { privateApi } from '@/lib/api-clients/rest-client';
import { toast } from 'sonner';
import { abbrFromSym } from '@/utils/token-symbol';
import { ErrorCode, ErrorMsgs } from '@/types';
import { useMarketStore } from '@/store/use-markets-store';
import { MutationConfig } from '@/lib/api-clients/react-query';

interface Params extends Leverage {
  options?: { skipToast?: boolean; waitForConfirm?: boolean };
}

export default function useUpdateLeverage(
  config: MutationConfig<(params: Params) => Promise<Leverage>> = {},
) {
  const { refetch: refetchAccount } = useFetchAccount();
  return useMutation({
    ...config,
    mutationFn: ({ symbol, value, options }: Params) => {
      const { getAccurateTime } = useAccountStore.getState();

      const restPromise: Promise<Leverage> = privateApi.post(
        '/account/leverage',
        {
          time: getAccurateTime(),
          value,
          symbol,
        },
      );

      const confirmPromise: Promise<Leverage> = restPromise.then(
        (updatedLvg) => {
          useMarketStore(symbol).setState({ leverage: updatedLvg.value });
          if (!options?.waitForConfirm) return Promise.resolve(updatedLvg);
          return new Promise((resolve, reject) => {
            let pollIntervalId: NodeJS.Timeout;
            let timeoutId: NodeJS.Timeout;

            const cleanup = () => {
              clearInterval(pollIntervalId);
              clearTimeout(timeoutId);
            };

            pollIntervalId = setInterval(() => {
              refetchAccount()
                .then(({ data: acc }) => {
                  const currentLeverage = acc?.leverages.find(
                    (lvg) => lvg.symbol === updatedLvg.symbol,
                  )?.value;
                  if (currentLeverage === updatedLvg.value) {
                    cleanup();
                    resolve(updatedLvg);
                  }
                })
                .catch((error) => {
                  reject(error);
                });
            }, 1_000);

            timeoutId = setTimeout(() => {
              cleanup();
              reject(ErrorCode.INTERNAL_CREATE_ORDER_TIMEOUT);
            }, 30_000); // 30 seconds timeout
          });
        },
      );

      if (!options?.skipToast) {
        toast.promise(confirmPromise, {
          success: (data) =>
            `Leverage for ${abbrFromSym(data.symbol)} successfully modified to ${data.value}X!`,
          error: (err) =>
            err === 1099
              ? 'Need to deposit first'
              : ErrorMsgs[err as ErrorCode],
          loading: 'Changing leverage...',
        });
      }

      return confirmPromise;
    },
  });
}
